import { useEffect, useState, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { graphql } from 'react-relay'
import { useMutation, fetchQuery, commitMutation } from 'react-relay'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { activeWalletUpdated, formatBalance, WalletConstant, Withdraw } from '../../reducers/wallet.reducer'
import ConfigurationService from '../../service/ConfigurationService'
import { store } from '../../store'
import { AppTypes } from '../../reducers/app.reducer'
import { formatFiat } from '../rightSide/walletComponent/WalletComponent'
import Env from '../../RelayEnvironment'
import { Currency } from './__generated__/DepositViewComponentDepositAddressQuery.graphql'

import 'react-toastify/dist/ReactToastify.css'

import { updateUserPreferenceMutation } from '@src/queries/__generated__/updateUserPreferenceMutation.graphql'
import { updateUserPreference } from '@src/queries/updateUserPreference'
import { DepositViewTokensQuery, DepositViewTokensQuery$data } from './__generated__/DepositViewTokensQuery.graphql'
import { listTokens } from './DepositView'
import { WithdrawViewComponentEstimateFeeQuery } from './__generated__/WithdrawViewComponentEstimateFeeQuery.graphql'
import { CancelWithdrawPopup, withdrawHistoryQuery } from './HistoryView'
import { HistoryViewComponentWithdrawsQuery } from './__generated__/HistoryViewComponentWithdrawsQuery.graphql'
import React from 'react'

import './WithdrawView.scoped.scss'
import './HistoryView.scoped.scss'
import { getLogoForCoin } from '@src/service/coins/coins'

import { UserProp } from '@src/stores/auth/user'
import { getUserPreference, addUserPreference } from '@src/stores/auth/preferences'
import { getAuthState } from '@src/stores/auth'
import WithdrawButton from './WithdrawView.button'
import AddressInput from './WithdrawView.address'
import MessageBlock from './WithdrawView.message'
import Units from './WithdrawView.units'
import NetworkSelect from './WithdrawView.network'
import WithdrawHistory from './WithdrawView.history'
import { WebpLazyImage } from '../WebpLazyImage/WepbLazyImage'

export const WithdrawView = ({ openListView, setPopup }) => {
  const { t } = useTranslation()
  const walletStore = store.getState().wallet
  const [activeWallet, setActiveWallet] = useState(walletStore?.activeWallet)

  const [commit] = useMutation<updateUserPreferenceMutation>(updateUserPreference)

  const [amount, setAmount] = useState<string>()
  const [address, setAddress] = useState<string>()
  const [memo, setMemo] = useState<string>()
  const [fee, setFee] = useState<string>()
  const [isLoadingSubmission, setIsLoadingSubmission] = useState(false)
  const isExternalCurrency = ConfigurationService.instance.isExternal({ key: activeWallet?.currency })
  const [tokens, setTokens] = useState<DepositViewTokensQuery$data>()
  const [network, setNetwork] = useState<string>()
  const [pastWithdraws, setPastWithdraws] = useState<Withdraw[]>([])

  const HandleClick = (uuid) => {
    setPopup(
      <CancelWithdrawPopup
        uuid={uuid}
        pastWithdraws={pastWithdraws}
        setPastWithdraws={setPastWithdraws}
        setPopup={setPopup}
      />
    )
  }

  const showNotification = (status, text) =>
    store.dispatch({ type: AppTypes.SHOW_NOTIFICATION, notification: { status: status, text: text } })

  const clearForm = () => {
    setAddress('')
    setAmount('')
  }

  const fetchPastWithdraws = useCallback(() => {
    if (!activeWallet) return
    fetchQuery<HistoryViewComponentWithdrawsQuery>(Env, withdrawHistoryQuery, {
      filter: { currency: activeWallet.currency as Currency },
      page: 1,
    }).subscribe({
      next: ({ withdraws }) => {
        if (withdraws?.entries) {
          setPastWithdraws([...withdraws.entries])
        }
      },
    })
  }, [activeWallet])

  useEffect(() => {
    fetchPastWithdraws()
  }, [activeWallet, fetchPastWithdraws])

  useEffect(() => {
    const subscription = activeWalletUpdated.subscribe(() => {
      setActiveWallet(store.getState().wallet.activeWallet)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [activeWallet])

  useEffect(() => {
    fetchQuery<DepositViewTokensQuery>(Env, listTokens, {}).subscribe({
      next: (data) => {
        setTokens(data)
        if (isExternalCurrency && activeWallet) {
          // const token = data.tokens.find(x => x.crypto === activeWallet.currency);
          setNetwork(undefined)
        }
      },
    })
  }, [activeWallet, isExternalCurrency])

  useEffect(() => {
    const isExternal = ConfigurationService.instance.isExternal({ key: activeWallet?.currency })

    if (
      activeWallet &&
      (ConfigurationService.instance.getBTCLikeCurrencies().indexOf(activeWallet.currency) >= 0 || isExternal)
    ) {
      if (activeWallet?.currency === 'FUN') {
        // Imaginary coin
        return
      }
      const args = {
        currency: activeWallet.currency as Currency,
      }
      if (isExternal) {
        if ((network?.length || 0) > 0 && (address?.length || 0) > 0) {
          args['tokenId'] = network
          args['address'] = address
        } else {
          return
        }
      }
      fetchQuery<WithdrawViewComponentEstimateFeeQuery>(Env, feeEstimateQuery, args).subscribe({
        next: ({ estimateFee }) => {
          if (estimateFee && estimateFee.length === 1) {
            setFee(estimateFee[0].feerate?.toFixed(8))
          }
        },
      })
    }
  }, [activeWallet, network, address])

  if (!activeWallet) {
    return <span>...</span>
  }

  const coins = ConfigurationService.instance.getCurrencies()
  const unitConfig = coins[activeWallet.currency]
  const defaultUnit = ConfigurationService.instance.getCurrency(activeWallet.currency).format
  const selectedUnit = getUserPreference(
    ('unit_' + activeWallet.currency.toUpperCase()) as UserProp,
    (value) => value || defaultUnit
  )

  const setActive = (e: React.MouseEvent | undefined, currency: string) => {
    e?.preventDefault()
    e?.stopPropagation()

    const w = store
      .getState()
      .wallet.wallets.find((x) => x.currency.toLowerCase() === currency.toLowerCase() && x.virtualId === 0)
    if (!w) return

    store.dispatch({ type: WalletConstant.SET_ACTIVE_WALLET, wallet: w })
    commitMutation<updateUserPreferenceMutation>(Env, {
      mutation: updateUserPreference,
      variables: { input: { key: UserProp.ACTIVE_COIN, value: w.currency } },
    })
  }

  const setActiveUnit = (e: React.MouseEvent, currency: string, unit: string) => {
    e.preventDefault()
    e.stopPropagation()
    setActive(undefined, currency)
    commit({
      variables: { input: { key: 'unit_' + currency, value: unit } },
      onCompleted: (resp) => {
        if (!resp.storeUserPreference) return
        const { preference } = resp.storeUserPreference

        const state = getAuthState()
        switch (state.type) {
          case 'LoggedIn':
            addUserPreference(preference)
            break
          default:
            throw new Error('Not logged in')
        }
      },
    })
  }
  const coin = tokens?.tokens.find((x) => x.crypto === activeWallet.currency)
  const customStyles = {
    control: (provided) => ({
      ...provided,
      color: 'white',
      backgroundColor: 'unset',
      border: '1px solid #fff',
      borderRadius: '20px',
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '10px 10px',
      backgroundColor: '#212226',
      color: '#fff',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#212226',
    }),
  }

  const filterOption = (candidate, input) => {
    input = input.toLowerCase()
    return undefined !== Object.values(candidate.data).find((x: any) => x.toString().toLowerCase().indexOf(input) >= 0)
  }

  const getIsWithdrawLimit = ({ limit = 0 }) => {
    if (Number(amount) < limit) {
      return { isWithdrawLimit: true, limit }
    }

    return { isWithdrawLimit: false, limit }
  }

  const { isWithdrawLimit, limit } = getIsWithdrawLimit({ limit: unitConfig.withdraw_limit })

  if (activeWallet?.currency === 'JACK') {
    // Imaginary coin
    return <h2>{t('general.comingSoon', 'Coming soon')}</h2>
  }

  return (
    <div className="wrapperWallet">
      <div className="with-table">
        <div>
          <span>{t('wallet.amount', 'Amount')}</span>
          <span>{t('wallet.balance', 'Balance')}</span>
          <span className="coin-header">{t('wallet.coin', 'Coin')}</span>
        </div>
        <div className="balance-row">
          <div className="withdraw-amount ">
            <input type="number" step={1} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="balance">
            <span onClick={() => setAmount(formatBalance(activeWallet, false, false))}>
              {formatBalance(activeWallet, false, false)}
            </span>
            {formatFiat(walletStore.rates, activeWallet)}
          </div>
          <div>
            <div className="select" onClick={openListView}>
              <div className="currency">
                <WebpLazyImage className="coin-image" alt={activeWallet.currency} src={getLogoForCoin(activeWallet.currency)} />
                {activeWallet.currency}
              </div>
              <FontAwesomeIcon icon={faChevronDown} />
            </div>
          </div>
        </div>
      </div>
      <Units
        unitConfig={unitConfig}
        selectedUnit={selectedUnit}
        activeWallet={activeWallet}
        setActiveUnit={setActiveUnit}
        show={activeWallet?.currency !== 'FUN'}
      />
      <AddressInput address={address} onChange={setAddress} show={activeWallet?.currency !== 'FUN'} />
      <NetworkSelect
        network={network}
        setNetwork={setNetwork}
        setMemo={setMemo}
        memo={memo}
        coin={coin}
        customStyles={customStyles}
        show={!!isExternalCurrency}
      />
      <MessageBlock type="fee" show={!!fee}>
        {t('wallet.withdrawView.estimatedFee', 'Estimated fee')}: {fee} {activeWallet.currency}
      </MessageBlock>
      <MessageBlock type="error" show={isWithdrawLimit}>
        <Trans i18nKey="wallet.withdrawView.withdrawLimit" values={{ limit, currency: activeWallet.currency }}>
          Minimum witdrawal is {{ withdrawLimit: limit }} {{ currency: activeWallet.currency }}
        </Trans>
      </MessageBlock>
      <MessageBlock type="warning" show={activeWallet?.currency !== 'FUN'}>
        {t('wallet.withdrawView.reminder', 'Make sure you have the correct adress. This cannot be undone.')}
      </MessageBlock>
      <WithdrawButton
        isLoadingSubmission={isLoadingSubmission}
        amount={amount}
        address={address}
        isExternalCurrency={isExternalCurrency}
        network={network}
        selectedUnit={selectedUnit}
        activeWallet={activeWallet}
        memo={memo}
        setPopup={setPopup}
        clearForm={clearForm}
        setIsLoadingSubmission={setIsLoadingSubmission}
        fetchPastWithdraws={fetchPastWithdraws}
        show={activeWallet?.currency !== 'FUN'}
      />
      <WithdrawHistory
        pastWithdraws={pastWithdraws}
        onCancelClick={HandleClick}
        show={activeWallet?.currency !== 'FUN'}
      />
    </div>
  )
}

const feeEstimateQuery = graphql`
  query WithdrawViewComponentEstimateFeeQuery($currency: Currency!, $address: String, $tokenId: String) {
    estimateFee(currency: $currency, address: $address, tokenId: $tokenId) {
      blocks
      feerate
    }
  }
`
